import { ArrowRight, Check, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

export function HeroEmailSignup() {
  const [status, setStatus] = useState<"idle" | "input" | "loading" | "success">("idle");
  const [email, setEmail] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (status === "input" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [status]);

  useEffect(() => {
    if (status === "success") {
      const timer = setTimeout(() => {
        setStatus("idle");
        setEmail("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus("loading");

    try {
      const response = await fetch("/api/sign-up", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      // Parse response
      const data = await response.json() as { error?: string; success?: boolean };

      if (!response.ok) {
        // Just log or shake in a real app, for now we'll just go back to input or stay in loading?
        // Let's handle error by staying in input and maybe focusing
        console.error(data.error);
        setStatus("input");
        // Ideally show an error tooltip
        return;
      }

      setStatus("success");
    } catch (error) {
      console.error(error);
      setStatus("input");
    }
  };

  return (
    <motion.div
      layout
      transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
      className={cn(
        "relative bg-white rounded-full inline-flex items-center overflow-hidden shadow-sm",
        status === "idle"
          ? "hover:bg-gray-200 cursor-pointer"
          : "cursor-default",
        status === "success" && "bg-white/90"
      )}
      style={{ height: 48 }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {status === "idle" && (
          <motion.button
            layout="position"
            key="btn-idle"
            onClick={() => setStatus("input")}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="px-[24px] h-full text-black font-bold text-[13px] tracking-[0.05em] whitespace-nowrap bg-transparent border-none outline-none"
          >
            GET COPLANE
          </motion.button>
        )}

        {status === "success" && (
          <motion.div
            layout="position"
            key="btn-success"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="px-[24px] h-full text-black/60 font-bold text-[13px] tracking-[0.05em] whitespace-nowrap flex items-center gap-2"
          >
            <span>WE'LL BE IN TOUCH</span>
            <Check className="w-4 h-4" />
          </motion.div>
        )}

        {(status === "input" || status === "loading") && (
          <motion.form
            key="form"
            layout="position"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onSubmit={handleSubmit}
            className="flex items-center pl-5 pr-1.5 h-full"
          >
            <input
              ref={inputRef}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === "loading"}
              className="outline-none bg-transparent text-black text-[15px] w-[220px] placeholder:text-gray-400 placeholder:font-normal"
              placeholder="email@company.com"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="w-9 h-9 bg-black rounded-full flex items-center justify-center text-white ml-2 hover:bg-black/80 transition-colors disabled:opacity-70"
            >
              {status === "loading" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4" />
              )}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

